import { useState, useEffect } from 'react';
import { PromoCodeOnlyBadge } from './PromoCodeOnlyBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Pencil } from 'lucide-react';
import { ProductVariation } from '../types';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { useGuestMode } from '../hooks/useGuestMode';
import { useUser } from '../contexts/UserContext';
import { Modal, Button, ImageGallery, Toast } from './ui';
import { ReorderRequestModal } from './ReorderRequestModal';
import { gtm } from '../services/gtm';

function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function QuickViewModal() {
  const { state, dispatch } = useShopping();
  const { clientData } = useClientDataContext();
  const { isGuest } = useGuestMode();
  const { user } = useUser();
  const storeIsOpen = !isGuest || clientData?.store?.isOpen !== false;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderSuccessToast, setReorderSuccessToast] = useState(false);

  const { quickViewProduct, isQuickViewOpen } = state;

  useEffect(() => {
    if (isQuickViewOpen && quickViewProduct) {
      const firstVariation = quickViewProduct.variations[0] ?? {
        id: quickViewProduct.id,
        size: 'Standard',
        color: 'Default',
        price: quickViewProduct.basePrice,
        stock: quickViewProduct.stock ?? 0,
      };
      gtm.pushViewItem(quickViewProduct, firstVariation);
    }
  }, [isQuickViewOpen, quickViewProduct]);

  // Get current images based on hovered or selected variation
  const getCurrentImages = () => {
    if (!quickViewProduct) return [];

    // If hovering over a color, show that variation's images
    if (hoveredColor) {
      const hoveredVariation = quickViewProduct.variations.find(v =>
        v.color === hoveredColor && (selectedVariation ? v.size === selectedVariation.size : true)
      );
      if (hoveredVariation?.images && hoveredVariation.images.length > 0) {
        return hoveredVariation.images;
      }
    }

    // Otherwise show selected variation images
    if (selectedVariation?.images && selectedVariation.images.length > 0) {
      return selectedVariation.images;
    }
    return quickViewProduct.images;
  };

  if (!isQuickViewOpen || !quickViewProduct) return null;

  const hasVariations = quickViewProduct.variations.length > 0;
  const needsSelection = quickViewProduct.variations.length > 1;
  // Auto-select only when there's exactly one variation; otherwise require explicit choice
  const variation = selectedVariation ?? (needsSelection ? null : quickViewProduct.variations[0]);
  const minOrderQty = quickViewProduct.minOrderQty && quickViewProduct.minOrderQty > 1 ? quickViewProduct.minOrderQty : 1;

  // For products without variations, use product-level stock
  const effectiveStock = hasVariations ? (variation?.stock ?? 0) : (quickViewProduct.stock ?? 0);

  // Can order if: backorder/ondemand allowed OR stock >= minOrderQty; also requires variation selected when needsSelection
  const canOrder = (quickViewProduct.allowBackorder || quickViewProduct.allowOnDemand || effectiveStock >= minOrderQty)
    && (!needsSelection || !!selectedVariation);

  const handleAddToCart = () => {
    if (quickViewProduct.isPrintDetail && quickViewProduct.printDetailType === 'calendar') {
      dispatch({
        type: 'SHOW_TOAST',
        payload: {
          id: `${Date.now()}-calendar-error`,
          message: 'Please customize your calendar before adding to cart',
          productName: quickViewProduct.name,
          productImage: quickViewProduct.images[0],
          type: 'error',
        },
      });
      return;
    }
    if (quickViewProduct.isPrintDetail && quickViewProduct.printDetailType === 'business_card') {
      dispatch({
        type: 'SHOW_TOAST',
        payload: {
          id: `${Date.now()}-businesscard-error`,
          message: 'Please customize your business card before adding to cart',
          productName: quickViewProduct.name,
          productImage: quickViewProduct.images[0],
          type: 'error',
        },
      });
      return;
    }

    if (hasVariations && variation) {
      gtm.pushAddToCart(quickViewProduct, variation, minOrderQty);
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product: quickViewProduct, variation, quantity: minOrderQty }
      });
      dispatch({ type: 'CLOSE_QUICK_VIEW' });
    } else if (!hasVariations) {
      // For products without variations, create a default variation
      const defaultVariation = {
        id: `${quickViewProduct.id}-default`,
        size: '',
        color: '',
        price: quickViewProduct.basePrice,
        stock: quickViewProduct.stock ?? 0
      };
      gtm.pushAddToCart(quickViewProduct, defaultVariation, minOrderQty);
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          product: quickViewProduct,
          variation: defaultVariation,
          quantity: minOrderQty
        }
      });
      dispatch({ type: 'CLOSE_QUICK_VIEW' });
    }
  };

  const handleClose = () => {
    dispatch({ type: 'CLOSE_QUICK_VIEW' });
    setSelectedVariation(null);
  };

  const handleViewFullProduct = () => {
    dispatch({ type: 'CLOSE_QUICK_VIEW' });
    // Preserve department and category IDs from current URL
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    navigate(`/product/${quickViewProduct.id}${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <>
    <Modal
      isOpen={isQuickViewOpen}
      onClose={handleClose}
      size="md"
      closeOnBackdrop={true}
      closeOnEscape={true}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image - Smaller */}
        <div className="sm:w-1/2">
          <ImageGallery
            images={getCurrentImages()}
            alt={quickViewProduct.name}
            showThumbnails={false}
            enableKeyboardNavigation={false}
          />
        </div>

        {/* Product Details - Compact */}
        <div className="sm:w-1/2 p-4">
          <div className="flex items-start justify-between gap-2 mb-2 pr-8">
            <h2 className="text-lg font-bold text-gray-900">
              {quickViewProduct.name}
            </h2>
            {user?.globalAdmin && (
              <a
                href={`/adminpanel/resources/products/${quickViewProduct.id}/edit`}
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0 inline-flex items-center justify-center p-1.5 rounded-md border border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                title="Edit in Admin"
              >
                <Pencil className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          {quickViewProduct.promoCodeOnly && (
            <PromoCodeOnlyBadge className="mb-3" />
          )}

          {/* Price */}
          <div className="mb-4">
            <span className="text-xl font-bold text-gray-900">
              ${variation?.price ?? quickViewProduct.basePrice}
            </span>
          </div>

          {/* Quick Variations */}
          <div className="space-y-3 mb-4">
            {/* Size Selection */}
            {quickViewProduct.variations
                    .filter(v => v.size.trim() !== '')
                    .some(v => v.size !== 'Standard')
                && (
              <div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">Size</h3>
                <div className="flex flex-wrap gap-2">
                    {Array.from(
                        new Set(quickViewProduct.variations
                            .filter(v => v.size.trim() !== '')
                            .map(v => v.size))
                    ).map(size => {
                      const hasStock = quickViewProduct.allowBackorder || quickViewProduct.allowOnDemand ||
                        quickViewProduct.variations.some(v => v.size === size && v.stock > 0);
                      const showOutOfStock = !hasStock && !quickViewProduct.allowOnDemand;

                      return (
                        <button
                          key={size}
                          onClick={() => {
                            const newVariation = quickViewProduct.variations.find(v =>
                              v.size === size && (selectedVariation ? v.color === selectedVariation.color : true)
                            ) || quickViewProduct.variations.find(v => v.size === size);
                            setSelectedVariation(newVariation || null);
                          }}
                          disabled={!hasStock}
                          className={`px-2 py-1 text-xs border rounded font-medium transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            !hasStock
                              ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : variation?.size === size
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          aria-label={`Select size ${size}${showOutOfStock ? ' (out of stock)' : ''}`}
                          aria-pressed={variation?.size === size}
                          aria-disabled={!hasStock}
                        >
                          {!hasStock ? <span className="line-through">{size}</span> : size}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {(() => {
              const selectedSize = variation?.size;
              const colors = Array.from(new Set(quickViewProduct.variations.map(v => v.color)))
                .filter(c => c && c !== 'Default')
                .filter(c => !selectedSize || quickViewProduct.variations.some(v => v.color === c && v.size === selectedSize));
              return colors.length > 0 ? (
                <div>
                  <h3 className="text-xs font-medium text-gray-900 mb-1">Color</h3>
                  <div className="flex flex-wrap gap-2">
                      {colors.map(color => {
                        const availableForSize = !selectedSize || quickViewProduct.variations.some(v => v.color === color && v.size === selectedSize);
                        const hasStock = availableForSize && (quickViewProduct.allowBackorder || quickViewProduct.allowOnDemand ||
                          quickViewProduct.variations.some(v => v.color === color && (!selectedSize || v.size === selectedSize) && v.stock > 0));
                        const showOutOfStock = !hasStock && !quickViewProduct.allowOnDemand;

                        const bgColor = quickViewProduct.variations.find(v => v.color === color)?.hex || '#E5E7EB';
                        const textColor = getTextColor(bgColor);
                        return (
                          <button
                            key={color}
                            onClick={() => {
                              const newVariation = quickViewProduct.variations.find(v =>
                                v.color === color && (selectedVariation ? v.size === selectedVariation.size : true)
                              ) || quickViewProduct.variations.find(v => v.color === color);
                              setSelectedVariation(newVariation || null);
                            }}
                            onMouseEnter={() => !hasStock ? null : setHoveredColor(color)}
                            onMouseLeave={() => setHoveredColor(null)}
                            disabled={!hasStock}
                            style={{
                              backgroundColor: hasStock ? bgColor : '#F3F4F6',
                              color: hasStock ? textColor : '#9CA3AF',
                              borderColor: variation?.color === color ? '#2563EB' : '#D1D5DB',
                              opacity: hasStock ? 1 : 0.6
                            }}
                            className={`px-2 py-1 text-xs border rounded font-medium transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              !hasStock
                                ? 'cursor-not-allowed'
                                : variation?.color === color
                                ? 'ring-2 ring-blue-600'
                                : 'hover:border-gray-400'
                            }`}
                            aria-label={`Select ${color} color${showOutOfStock ? ' (out of stock)' : ''}`}
                            aria-pressed={variation?.color === color}
                            aria-disabled={!hasStock}
                          >
                            {!hasStock ? <span className="line-through">{color}</span> : color}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {/* Stock Info */}
          <p className="text-xs text-gray-500 mb-4">
            {effectiveStock > 0 ? `${effectiveStock} in stock` : quickViewProduct.allowOnDemand ? 'Available on demand' : 'Out of stock'}
          </p>

          {/* Action Buttons - Compact */}
          <div className="space-y-2">
            {needsSelection && !selectedVariation && (
              <p className="text-xs text-amber-600 font-medium">
                Please select your options above
              </p>
            )}
            <Button
              onClick={handleAddToCart}
              disabled={!storeIsOpen || !canOrder}
              variant="primary"
              size="sm"
              fullWidth={true}
              icon={<ShoppingCart className="w-4 h-4" />}
            >
              {storeIsOpen ? 'Quick Add' : 'Store Closed'}
            </Button>
            
            <Button
              onClick={handleViewFullProduct}
              variant="outline"
              size="sm"
              fullWidth={true}
            >
              View Full Details
            </Button>

            {clientData?.enableRequestReorder && (user?.clientAdmin || user?.globalAdmin || user?.distributorAdmin) && (
              <Button
                onClick={() => setReorderModalOpen(true)}
                variant="secondary"
                size="sm"
                fullWidth={true}
              >
                Request Reorder
              </Button>
            )}
          </div>
        </div>
      </div>

      <ReorderRequestModal
        isOpen={reorderModalOpen}
        productId={quickViewProduct.id}
        onClose={() => setReorderModalOpen(false)}
        onSuccess={() => {
          setReorderModalOpen(false);
          setReorderSuccessToast(true);
        }}
      />
    </Modal>

    {reorderSuccessToast && (
      <Toast
        message="Your reorder request has been submitted. Your account rep will be in touch."
        productName={quickViewProduct.name}
        productImage={quickViewProduct.images[0] ?? ''}
        type="success"
        duration={5000}
        onClose={() => setReorderSuccessToast(false)}
      />
    )}
    </>
  );
}